import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ArbzgAckRequest, CancelAbsenceRequest, CloseTimeAccountRequest, CorrectAttendanceRequest, CreateAbsenceRequest,
  DecideAbsenceRequest, ForfeitVacationRequest, ReopenTimeAccountRequest, ResolveCorrectionRequest,
  SendVacationNoticeRequest, SetWorkPlanRequest,
  CreateAbsenceSeriesRequest,
  CancelAbsenceSeriesRequest,
} from '@wf/shared'
import { api } from '../api/client'

// Query-Hooks des „Zeiten"-Tabs. Schlüssel-Disziplin: alles unter ['zeiten', …] bzw. den bestehenden
// ['attendance', …]-Ästen, damit Chef-Korrekturen gezielt Report/Timesheet/Statistik invalidieren können,
// ohne unbeteiligte Screens (Stempeluhr nutzt ['attendance','me']) zu clobbern.

/** Zeitnachweis (Tageszeilen + Roh-Stempel). Eigener für jeden; fremder userId nur Chef/Zweitchef (Server erzwingt). */
export function useAttendanceReport(from: string, to: string, userId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['zeiten', 'report', userId ?? 'me', from, to],
    queryFn: () => api.attendanceReport(from, to, userId),
    enabled: enabled && !!from && !!to,
  })
}

/** Soll/Ist je Kalenderwoche (Dienstplan vs. Stempel). */
export function useTimesheet(from: string, to: string, userId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['zeiten', 'timesheet', userId ?? 'me', from, to],
    queryFn: () => api.timesheet(from, to, userId),
    enabled: enabled && !!from && !!to,
  })
}

/**
 * § 3 ArbZG-Durchschnitt (rollierendes Ausgleichsfenster). Eigener für jeden; fremde nur für den
 * Freigabe-Kreis — der Server erzwingt es, das `enabled` hier ist reine UX.
 * KEIN Zeitraum im Schlüssel: das Fenster endet immer heute, ein Parameter dafür existiert bewusst nicht.
 */
export function useArbzgAverage(userId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['zeiten', 'durchschnitt', userId ?? 'me'],
    queryFn: () => api.arbzgAverage(userId),
    enabled,
  })
}

/** § 3-Durchschnitt MEHRERER Mitarbeiter parallel (Auswertung). Query-Keys identisch zu useArbzgAverage,
 *  damit Karte und Team-Überblick sich den Cache teilen. */
export function useTeamArbzgAverage(userIds: readonly number[], enabled: boolean) {
  return useQueries({
    queries: userIds.map((id) => ({
      queryKey: ['zeiten', 'durchschnitt', id],
      queryFn: () => api.arbzgAverage(id),
      enabled,
    })),
  })
}

/** Korrektur-Posteingang des Chefs (offene zuerst). Nur isBoss — der Aufrufer gated per `enabled`. */
export function useCorrectionsInbox(enabled: boolean) {
  return useQuery({ queryKey: ['zeiten', 'corrections'], queryFn: api.listCorrections, enabled })
}

/** Zeitnachweise MEHRERER Mitarbeiter parallel (Chef-Timeline 1b / Auswertung 1c). Nur isBoss —
 *  der Aufrufer gated per `enabled`; Query-Keys identisch zu useAttendanceReport (geteilter Cache). */
export function useTeamReports(userIds: readonly number[], from: string, to: string, enabled: boolean) {
  return useQueries({
    queries: userIds.map((id) => ({
      queryKey: ['zeiten', 'report', id, from, to],
      queryFn: () => api.attendanceReport(from, to, id),
      enabled: enabled && !!from && !!to,
    })),
  })
}

/** Soll/Ist MEHRERER Mitarbeiter parallel (Auswertung 1c). Nur isBoss — Aufrufer gated per `enabled`. */
export function useTeamTimesheets(userIds: readonly number[], from: string, to: string, enabled: boolean) {
  return useQueries({
    queries: userIds.map((id) => ({
      queryKey: ['zeiten', 'timesheet', id, from, to],
      queryFn: () => api.timesheet(from, to, id),
      enabled: enabled && !!from && !!to,
    })),
  })
}

/** Zeitkonto (abgeschlossene Monats-Perioden) eines Nutzers. */
export function useTimeAccount(userId: number | undefined) {
  return useQuery({ queryKey: ['zeiten', 'account', userId ?? 'me'], queryFn: () => api.timeAccount(userId) })
}

/** Monat hart abschließen (Hard-Close). Frischt Konto + Timesheet auf — AUCH nach Fehlern (Write-Contract:
 *  bei 409/„bereits abgeschlossen" reconcilen statt einen veralteten Button weiter anzubieten, Review-Fix). */
export function useCloseTimeAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CloseTimeAccountRequest) => api.closeTimeAccount(body),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['zeiten'] }),
  })
}

/** Jüngsten Abschluss binnen 24 h zurücknehmen. Reconcile auch im Fehlerfall (Fenster abgelaufen o. ä.). */
export function useReopenTimeAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ReopenTimeAccountRequest) => api.reopenTimeAccount(body),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['zeiten'] }),
  })
}

/** Dienstplan (Wochenmuster) eines Nutzers. */
export function useWorkPlan(userId: number | null) {
  return useQuery({
    queryKey: ['zeiten', 'workplan', userId],
    queryFn: () => api.getWorkPlan(userId as number),
    enabled: userId != null,
  })
}

/** Nach jeder Chef-Korrektur ist alles Zeit-Abgeleitete potenziell veraltet → gezielt neu laden. */
function invalidateZeiten(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['zeiten'] })
  // Eigene Stempeluhr kann betroffen sein, wenn der Chef den EIGENEN heutigen Stempel korrigiert.
  void qc.invalidateQueries({ queryKey: ['attendance'] })
}

/** Stempel entwerten/nachtragen (isBoss, Pflicht-Grund) — revisionssicher, Server auditiert.
 *  Invalidierung auch im FEHLERFALL (onSettled): ein 409 („bereits entwertet"/Versions-Rennen) heißt,
 *  der Server-Stand ist weiter als unsere Anzeige → reconcilen statt stale weiterzeigen (Write-Contract). */
export function useCorrectAttendance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CorrectAttendanceRequest) => api.correctAttendance(body),
    onSettled: () => invalidateZeiten(qc),
  })
}

/** Korrekturanfrage abschließen (erledigt/abgelehnt). */
export function useResolveCorrection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; body: ResolveCorrectionRequest }) => api.resolveCorrection(vars.id, vars.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zeiten', 'corrections'] }),
  })
}

/** ArbZG-Verstoß quittieren (R4/1.10). Nach dem Ack den Zeitnachweis auffrischen → das Flag kommt als acked zurück. */
export function useArbzgAck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ArbzgAckRequest) => api.arbzgAck(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zeiten', 'report'] }),
  })
}

/** Dienstplan setzen (Upsert/Delete je Wochentag) — Soll-Seite von Timesheet/Statistik hängt daran. */
export function useSetWorkPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SetWorkPlanRequest) => api.setWorkPlan(body),
    onSuccess: (_res, body) => {
      void qc.invalidateQueries({ queryKey: ['zeiten', 'workplan', body.userId] })
      void qc.invalidateQueries({ queryKey: ['zeiten', 'timesheet'] })
    },
  })
}

// ---- Abwesenheiten (Phase 2, Block 1) ----

export function useAbsenceTypes() {
  return useQuery({ queryKey: ['zeiten', 'absence-types'], queryFn: api.absenceTypes })
}

export function useMyAbsences() {
  return useQuery({ queryKey: ['zeiten', 'absences', 'me'], queryFn: () => api.listAbsences('me') })
}

export function useTeamAbsences(month: string, enabled = true) {
  return useQuery({ queryKey: ['zeiten', 'absences', 'team', month], queryFn: () => api.listAbsences('team', month), enabled })
}

export function useCreateAbsence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAbsenceRequest) => api.createAbsence(body),
    onSettled: () => invalidateZeiten(qc), // Wirkung steckt in Timesheet/Nachweis → alles Zeit-Abgeleitete neu
  })
}

export function useDecideAbsence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; body: DecideAbsenceRequest }) => api.decideAbsence(vars.id, vars.body),
    onSettled: () => invalidateZeiten(qc),
  })
}

export function useCancelAbsence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; body: CancelAbsenceRequest }) => api.cancelAbsence(vars.id, vars.body),
    onSettled: () => invalidateZeiten(qc),
  })
}

/** Serien eines Mitarbeiters (Azubi-Block) — fuer die Gruppierung und die Beenden-Aktion. */
export function useAbsenceSeries(userId?: number, enabled = true) {
  return useQuery({ queryKey: ['zeiten', 'absence-series', userId ?? 'alle'], queryFn: () => api.listAbsenceSeries(userId), enabled })
}

export function useCreateAbsenceSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAbsenceSeriesRequest) => api.createAbsenceSeries(body),
    // Eine Serie legt bis zu 100 genehmigte Zeilen an — jede Zeit-abgeleitete Ansicht ist danach veraltet.
    onSettled: () => invalidateZeiten(qc),
  })
}

export function useCancelAbsenceSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; body: CancelAbsenceSeriesRequest }) => api.cancelAbsenceSeries(vars.id, vars.body),
    onSettled: () => invalidateZeiten(qc),
  })
}

export function useVacation(userId?: number, year?: number) {
  // year gehört in den Key: der Rest-Chip fragt fremde Konten je ANTRAGSJAHR ab (Folgejahres-Planung).
  return useQuery({ queryKey: ['zeiten', 'vacation', userId ?? 'me', year ?? 'current'], queryFn: () => api.vacation(userId, year) })
}

export function useVacationAssistant(enabled = true) {
  return useQuery({ queryKey: ['zeiten', 'vacation-assistant'], queryFn: () => api.vacationAssistant(), enabled })
}

export function useSendVacationNotice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SendVacationNoticeRequest) => api.sendVacationNotice(body),
    onSettled: () => invalidateZeiten(qc),
  })
}

export function useAckVacationNotice() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => api.ackVacationNotice(id), onSettled: () => invalidateZeiten(qc) })
}

export function useForfeitVacation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ForfeitVacationRequest) => api.forfeitVacation(body),
    onSettled: () => invalidateZeiten(qc),
  })
}
